import { Model, Question } from "survey-core";

export interface GlDecoratedSurveyAnswer {
  name: string;
  value: string | boolean;
  questionAsAsked?: Question;
}

export interface FlattenedSurveyAnswers {
  [key: string]: string | boolean;
  "active-cancer-diagnosis"?: boolean;
  "dna-transplant"?: string;
  "have-you-had-a-dna-transplant"?: string;
  "history-of-cancer"?: string;
  "have-you-ever-been-told-you-have-cancer"?: boolean;
  "cancer-type"?: string;
  "planning-pregnancy-within-a-year"?: boolean;
  "you-or-partner-pregnant-or-planning-in-24-months"?: string;
  "taking-5-plus-medications"?: boolean;
  "taking-following-types-medication"?: boolean;
  "taking-following-types-medication-with-not-sure"?: string;
  "taking-medication-want-to-learn"?: boolean;
  "taking-medication"?: string | boolean;
}

export interface SurveyPage {
  elements: Array<{
    name: string;
    defaultValue?: any;
    [key: string]: any;
  }>;
}

export interface SurveyJson {
  pages: SurveyPage[];
  [key: string]: any;
}

/**
 * Checks if a trigger answer condition is met when changing survey pages.
 *
 * @param survey - The survey model instance
 * @param options - The page changing options
 * @param nameOfQuestionToCheck - The name of the question to check
 * @param answerEvaluatorMethod - Function to evaluate the answer
 * @returns True if the trigger condition is met
 */
export const checkForTriggerAnswer = (
  survey: Model,
  options: {
    oldCurrentPage: { elements: Array<{ name: string }> };
    isNextPage: boolean;
  },
  nameOfQuestionToCheck: string,
  answerEvaluatorMethod: (value: any) => boolean
): boolean => {
  if (
    options.oldCurrentPage.elements
      .map((e) => e.name)
      .includes(nameOfQuestionToCheck)
  ) {
    const question = survey.getQuestionByName(nameOfQuestionToCheck);
    return (
      question && options.isNextPage && answerEvaluatorMethod(question.value)
    );
  }
  return false;
};

/**
 * Replaces the default value of a question in a survey JSON structure.
 *
 * @param surveyJson - The survey JSON structure
 * @param questionName - The name of the question to update
 * @param newValue - The new default value
 * @returns Updated survey JSON
 */
export const replaceAnswerValueInSurveyJsonForQuestionByName = (
  surveyJson: SurveyJson,
  questionName: string,
  newValue: any
): SurveyJson => {
  const jsonToUpdate = { ...surveyJson };
  jsonToUpdate.pages.forEach((page) => {
    const element = page.elements.find(
      (element) => element.name === questionName
    );
    if (element) {
      element.defaultValue = newValue;
    }
  });
  return jsonToUpdate;
};

/**
 * Maps survey answer values based on their type.
 *
 * @param type - The type of the answer
 * @param value - The value to map
 * @returns Mapped value
 */
export const mapSurveyAnswerByType = (
  type: string,
  value: string | boolean
): boolean | string => {
  switch (type) {
    case "boolean":
      return value === "True";
    default:
      return value;
  }
};

/**
 * Rehydrates a survey with previous answers.
 *
 * @param survey - The survey model instance
 * @param previousAnswers - Array of previous answers
 */
export const rehydrateSurveyWithAnyPreviousAnswers = (
  survey: Model,
  previousAnswers: GlDecoratedSurveyAnswer[] | null | undefined
): void => {
  previousAnswers?.forEach((previousAnswer) => {
    const question = survey.getQuestionByName(previousAnswer.name);
    if (question) {
      question.value = previousAnswer.value;
    }
  });
};

/**
 * Converts an array of survey answers into a flattened object
 * where the keys are the answer names and the values are the answer values.
 *
 * @param surveyAnswers - Array of survey answers with name and value properties
 * @returns A flattened object of survey answers
 */
export const flattenSurveyAnswers = (
  surveyAnswers: GlDecoratedSurveyAnswer[]
): FlattenedSurveyAnswers => {
  return surveyAnswers.reduce<FlattenedSurveyAnswers>(
    (acc, answer) => ({
      ...acc,
      [answer.name]: answer.value
    }),
    {}
  );
};

/**
 * Checks if a subset of answers exists in the survey answers.
 *
 * @param surveyAnswers - Array of survey answers
 * @param namesOfAnswersToExtract - Array of answer names to look for
 * @returns Object containing only the requested answers
 */
export const selectSubsetOfAnswersFromSurveyAnswers = (
  surveyAnswers: GlDecoratedSurveyAnswer[] | null | undefined,
  namesOfAnswersToExtract: string[]
): Partial<FlattenedSurveyAnswers> => {
  if (!surveyAnswers) return {};
  const flattened = flattenSurveyAnswers(surveyAnswers);
  return Object.fromEntries(
    Object.entries(flattened).filter(([key]) =>
      namesOfAnswersToExtract.includes(key)
    )
  );
};

/**
 * Confirms that all specified questions have been answered in the survey.
 *
 * @param surveyAnswers - Array of survey answers
 * @param namesOfAnswersToCheckFor - Array of answer names that must be present
 * @returns True if all specified questions have been answered
 */
export const confirmAllSpecifiedQuestionsHaveBeenAnswered = (
  surveyAnswers: GlDecoratedSurveyAnswer[] | null | undefined,
  namesOfAnswersToCheckFor: string[]
): boolean => {
  if (!surveyAnswers) return true;
  const subset = selectSubsetOfAnswersFromSurveyAnswers(
    surveyAnswers,
    namesOfAnswersToCheckFor
  );
  return Object.keys(subset).length === namesOfAnswersToCheckFor.length;
};

/**
 * Determines if a user has completed enough of the getting started survey
 * to proceed.
 *
 * Last updated with advent of precision-guidance-survey
 *
 * @param usersAnswers - Array of survey answers
 * @returns True if the user has completed enough of the survey
 */
export const hasUserCompletedEnoughOfGettingStartedSurvey = (
  usersAnswers: GlDecoratedSurveyAnswer[] | null | undefined
): boolean => {
  return confirmAllSpecifiedQuestionsHaveBeenAnswered(usersAnswers, [
    "have-you-ever-been-told-you-have-cancer",
    "have-you-had-a-dna-transplant",
    "taking-5-plus-medications",
    "taking-following-types-medication-with-not-sure",
    "you-or-partner-pregnant-or-planning-in-24-months"
  ]);
};

export const hasUserCompletedCologuardQualificationSurvey = (
  usersAnswers: GlDecoratedSurveyAnswer[] | null | undefined
): boolean => {
  return confirmAllSpecifiedQuestionsHaveBeenAnswered(usersAnswers, [
    "ever-had-colon-cancer-screen"
  ]);
};

export const hasUserCompletedCologuardPersonalizedResourcesSurvey = (
  usersAnswers: GlDecoratedSurveyAnswer[] | null | undefined
): boolean => {
  return confirmAllSpecifiedQuestionsHaveBeenAnswered(usersAnswers, [
    "learning-resource-interests"
  ]);
};

/**
 * Checks if the user has answered all critical kit request questions.
 * This includes questions about pregnancy planning and medication types.
 */
export const hasAnsweredAllCriticalKitRequestQuestions = (
  usersAnswers: GlDecoratedSurveyAnswer[] | null | undefined
): boolean => {
  return confirmAllSpecifiedQuestionsHaveBeenAnswered(usersAnswers, [
    "you-or-partner-pregnant-or-planning-in-24-months",
    "taking-following-types-medication-with-not-sure"
  ]);
};
